import { useCallback } from 'react';
import { useMixpanel } from '../context/MixpanelProvider';
import mixpanel from 'mixpanel-browser';
import { fetchStudies } from '../services/survey';
import { fetchInterviews } from '@services/interview';

export function useUserAnalytics() {
  const { aliasUser, identifyUser, trackEvent } = useMixpanel();

  const identifyUserWithProfile = useCallback(
    async (userData) => {
      if (!userData) return;

      const anonymousId = mixpanel.get_distinct_id();

      // Link anonymous user to authenticated user
      aliasUser(anonymousId, userData.id);

      // Create a standardized user profile object without fetching studies data

      // Handle different data structures: Google login (username) vs full profile (first_name, last_name)
      let userName = '';
      let firstName = '';
      let lastName = '';

      if (userData.first_name || userData.last_name) {
        // Full profile data
        firstName = userData.first_name || '';
        lastName = userData.last_name || '';
        userName = `${firstName} ${lastName}`.trim() || userData.username || userData.email;
      } else if (userData.username) {
        // Google login data - only has username
        userName = userData.username;
        // Try to split username into first/last if it contains space
        const nameParts = userData.username.split(' ');
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        } else {
          firstName = userData.username;
        }
      } else {
        // Fallback to email
        userName = userData.email;
        firstName = userData.email.split('@')[0];
      }

      const userProfile = {
        $email: userData.email,
        $name: userName,
        $first_name: firstName,
        $last_name: lastName,
        email: userData.email,
        role: userData.role,
        customer_id: userData.customer_id,
        last_login: new Date().toISOString(),
      };

      // Add optional properties if they exist
      if (userData.created_at) userProfile.account_created = userData.created_at;

      // Add subscription details if available
      if (userData.subscription_detail) {
        userProfile.subscription_name = userData.subscription_detail.name;
        userProfile.subscription_type = userData.subscription_detail.subscription_type;
        userProfile.subscription_interval = userData.subscription_detail.interval;
        userProfile.subscription_amount = userData.subscription_detail.amount;
        userProfile.is_trial = userData.subscription_detail.is_trial;
        userProfile.has_notified_trial_end = userData.subscription_detail.has_notified_trial_end;
        userProfile.cancellation_attempted = userData.subscription_detail.cancellation_attempted;
        userProfile.end_date = userData.subscription_detail.end_date;
        userProfile.credit_balance = userData.subscription_detail.credit_balance;
      }

      if (userData.created_studies) {
        userProfile.dynamic_surveys = userData.created_studies.dynamic_surveys || 0;
        userProfile.qualitative_studies = userData.created_studies.qualitative_studies || 0;
      }

      // Identify the user with the complete profile
      identifyUser(userData.id, userProfile);

      return userProfile;
    },
    [aliasUser, identifyUser],
  );

  // Separate function for fetching and updating analytics data when needed
  const updateAnalyticsWithStudiesData = useCallback(
    async (userId) => {
      try {
        // Fetch studies data only when explicitly needed
        const [surveysData, interviewsData] = await Promise.all([
          fetchStudies({ page: 0, pageSize: 100 }),
          fetchInterviews({ page: 0, pageSize: 100 }),
        ]);

        const surveySummaries = surveysData.data.map((survey, index) => {
          const { title, status, analysis_status, end_date, total_responses } = survey;
          return `Study ${index + 1}: ${title || 'Untitled'},  Status: ${status}, Analysis: ${analysis_status}, End: ${end_date || 'N/A'}, Responses: ${total_responses ?? 0}`;
        });

        const interviewSummaries = interviewsData.data.map((interview, index) => {
          const {
            title,
            status,
            analysis_status,
            end_date,
            total_responses,
            owner_id,
            total_human_responses,
          } = interview;
          return `Study ${index + 1}: ${title || 'Untitled'},  Owner Id: ${owner_id}, Status: ${status}, Analysis: ${analysis_status}, End: ${end_date || 'N/A'}, Responses: ${total_responses ?? 0}, Human Responses: ${total_human_responses ?? 0}`;
        });

        // Update user properties with studies data
        identifyUser(userId, {
          total_surveys: surveySummaries,
          total_interviews: interviewSummaries,
        });

        return { surveySummaries, interviewSummaries };
      } catch (error) {
        console.error('Failed to fetch analytics data:', error);
        return null;
      }
    },
    [identifyUser],
  );

  return {
    identifyUserWithProfile,
    updateAnalyticsWithStudiesData,
    trackEvent,
  };
}
